import {IceCandidate} from 'models/IceCandidate';
import {SourceInfo} from 'models/SourceInfo';
import {Stream} from 'models/Stream';
import {Platform} from 'react-native';
import {
  mediaDevices,
  RTCIceCandidate,
  RTCPeerConnection,
  RTCSessionDescription,
} from 'models/webRTCTypes';
import constants from '../constants';

const RECONNECT_TIMEOUT = 5000;

class PeerConnectionService {
  private pc_config = {
    iceServers: [
      {
        urls: `stun:${constants.OPENVIDU_SERVER}`,
      },
    ],
  };

  private peerConnection?: RTCPeerConnection;
  private candidates: IceCandidate[] = [];
  private isLocal: boolean;
  private id: string;
  private reconnectionTimeout: NodeJS.Timeout | undefined;

  onIceCandidateAdded: ((iceCandidate: IceCandidate) => void) | undefined;
  onLocalStreamAdded: ((stream: Stream) => void) | undefined;
  onRemoteStreamAdded: ((stream: Stream) => void) | undefined;

  onConnectionFailed: (() => void) | undefined;
  onConnectionConnected: (() => void) | undefined;

  constructor(isLocal: boolean, id: string) {
    this.isLocal = isLocal;
    this.id = id;
    this.peerConnection = new RTCPeerConnection(
      this.isLocal ? {iceServers: []} : this.pc_config,
    );
    this.setUpCallbacks();
  }

  disconnect = () => {
    console.log('disconnect', this.id);

    this.peerConnection?.close();
    this.peerConnection = undefined;
    this.onIceCandidateAdded = undefined;
    this.onLocalStreamAdded = undefined;
    this.onRemoteStreamAdded = undefined;
    this.onConnectionFailed = undefined;
    this.onConnectionConnected = undefined;
  };

  addIceCandidate = async (iceCandidate: IceCandidate) => {
    if (this.peerConnection?.connectionState === 'connected') {
      return;
    }

    if (this.peerConnection?.signalingState === 'stable') {
      this.peerConnection.addIceCandidate(new RTCIceCandidate(iceCandidate));
      return;
    }

    this.candidates.push(iceCandidate);
  };

  setAnswer = (sdpAnswer: string) => {
    const offerDescription = new RTCSessionDescription({
      sdp: sdpAnswer,
      type: 'answer' as any,
    });

    this.peerConnection?.setRemoteDescription(offerDescription);
  };

  createOffer = async () => {
    if (Platform.OS == 'web') {
      if (!this.isLocal) {
        // @ts-ignore
        this.peerConnection.addTransceiver('audio', {
          direction: 'recvonly',
        });
        // @ts-ignore
        this.peerConnection.addTransceiver('video', {
          direction: 'recvonly',
        });
      }
    }

    const res = await this.peerConnection?.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true,
    });

    const sdp = res as RTCSessionDescription;
    this.peerConnection?.setLocalDescription(sdp);
    return sdp;
  };

  runLocalMedia = async () => {
    let isFront = true;

    const sourceInfos = (await mediaDevices.enumerateDevices()) as SourceInfo[];

    let videoSourceId;
    for (let i = 0; i < sourceInfos.length; i++) {
      const sourceInfo = sourceInfos[i];
      if (
        sourceInfo.kind == 'videoinput' &&
        sourceInfo.facing == (isFront ? 'front' : 'environment')
      ) {
        videoSourceId = sourceInfo.deviceId;
      }
    }

    const nativeConstraints = {
      audio: true,
      video: {
        mandatory: {
          minWidth: 500,
          minHeight: 300,
          minFrameRate: 30,
        },
        facingMode: isFront ? 'user' : 'environment',
        optional: videoSourceId ? [{sourceId: videoSourceId}] : [],
      },
    };

    const webConstraints = {
      audio: true,
      video: true,
    };

    try {
      const mediaStream = await mediaDevices.getUserMedia(
        Platform.OS == 'web' ? webConstraints : nativeConstraints,
      );

      const stream = new Stream(mediaStream);
      if (Platform.OS == 'web') {
        stream.getTracks().forEach(track => {
          // @ts-ignore
          this.peerConnection.addTransceiver(track.kind, {
            direction: 'sendonly',
          });
          //@ts-ignore
          this.peerConnection.addTrack(track);
        });
      } else {
        this.peerConnection?.addStream(stream);
      }

      this.onLocalStreamAdded && this.onLocalStreamAdded(stream);
    } catch (e) {
      console.log('getUserMedia Error: ', e);
    }
  };

  runShareScreen = async () => {
    try {
      //@ts-ignore
      const mediaStream = (await mediaDevices.getDisplayMedia({
        video: true,
      })) as MediaStream;

      const stream = new Stream(mediaStream);
      if (Platform.OS == 'web') {
        stream.getTracks().forEach(track => {
          // @ts-ignore
          this.peerConnection.addTransceiver(track.kind, {
            direction: 'sendonly',
          });

          //@ts-ignore
          this.peerConnection.addTrack(track);
        });
      } else {
        this.peerConnection?.addStream(stream);
      }

      this.onLocalStreamAdded && this.onLocalStreamAdded(stream);
    } catch (error) {
      console.log('getUserMedia Error: ', error);
      throw error;
    }
  };

  private setUpCallbacks = () => {
    if (!this.peerConnection) return;

    this.peerConnection.onicecandidate = (e: any) => {
      const event = e as unknown as {candidate: IceCandidate};
      if (event.candidate) {
        this.onIceCandidateAdded?.(event.candidate);
      }
    };

    this.peerConnection.onconnectionstatechange = e => {
      if (!this.peerConnection) return;

      console.log(
        'this.peerConnection.connectionState',
        this.id,
        this.isLocal,
        this.peerConnection.connectionState,
        this.peerConnection.iceConnectionState,
        this.peerConnection.signalingState,
        this.peerConnection.iceGatheringState,
      );

      switch (this.peerConnection.connectionState) {
        case 'connecting':
          this.reconnectionTimeout = setTimeout(() => {
            console.log('reconnecting...');
            this.onConnectionFailed?.();
          }, RECONNECT_TIMEOUT);
          break;

        case 'failed':
        case 'disconnected':
          console.log('failed...');
          clearInterval(this.reconnectionTimeout);
          this.reconnectionTimeout = undefined;
          this.onConnectionFailed?.();
          break;

        case 'connected':
          console.log('connected peer');

          clearInterval(this.reconnectionTimeout);
          this.reconnectionTimeout = undefined;
          this.onConnectionConnected?.();
          break;

        default:
          break;
      }
    };

    this.peerConnection.oniceconnectionstatechange = (e: any) => {
      console.log('oniceconnectionstatechange');
    };

    this.peerConnection.onsignalingstatechange = () => {
      console.log(
        'onsignalingstatechange, signalingState',
        this.peerConnection?.signalingState,
      );

      if (this.peerConnection?.signalingState === 'stable') {
        this.candidates.forEach(item => {
          this.peerConnection?.addIceCandidate(new RTCIceCandidate(item));
        });
      }
    };

    this.peerConnection.onicecandidateerror = event => {
      console.log('onicecandidateerror', event);
    };

    if (Platform.OS == 'web') {
      if (!this.isLocal) {
        this.peerConnection.ontrack = (e: any) => {
          const stream = new Stream(e.streams[0]);
          this.onRemoteStreamAdded?.(stream);
          this.onRemoteStreamAdded = undefined;
        };
      }
    } else {
      this.peerConnection.onaddstream = e => {
        const event = e as unknown as {stream: MediaStream};
        const stream = new Stream(event.stream);
        this.onRemoteStreamAdded?.(stream);
      };
    }
  };
}

export default PeerConnectionService;
